import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["item", "bullet", "wrapper", "instructionsOverlay"]
  static values = {
    activeClass: String,
    autoscroll: Boolean,
    autoscrollInterval: Number,
    initialOverlayTimeout: Number,
    default: 3,
  }

  connect() {
    this.currentIndex = 0
    this.items = this.itemTargets
    if (this.autoscrollValue) {
      this.startAutoscroll()
    }

    this.intersectionObserver = new IntersectionObserver(
      this.handleIntersectionObserver.bind(this),
      {
        root: this.wrapperTarget,
        threshold: 0.75,
      }
    )

    this.items.forEach((item) => this.intersectionObserver.observe(item))

    // Initialize overlay functionality
    this.initializeOverlay()
  }

  disconnect() {
    clearInterval(this.interval)
    clearTimeout(this.overlayTimeout)
    this.intersectionObserver.disconnect()
  }

  startAutoscroll() {
    const interval = this.autoscrollIntervalValue * 1000
    this.interval = setInterval(() => this.next(), interval)
  }

  next(event) {
    event?.preventDefault()
    this.resetAutoscroll()
    this.hideOverlay()

    this.scrollToItem((this.currentIndex + 1) % this.items.length)
  }

  previous(event) {
    event?.preventDefault()
    this.resetAutoscroll()
    this.hideOverlay()

    this.scrollToItem((this.currentIndex - 1 + this.items.length) % this.items.length)
  }

  goTo(event) {
    event?.preventDefault()
    this.resetAutoscroll()
    this.hideOverlay()

    const index = parseInt(event.params.index, 10)
    if (isNaN(index)) return

    this.scrollToItem(index)
  }

  resetAutoscroll() {
    if (this.autoscrollValue) {
      clearInterval(this.interval)
      this.startAutoscroll()
    }
  }

  scrollToItem(index) {
    const wrapper = this.wrapperTarget
    const previousIndex = this.currentIndex
    const newIndex = index
    const isWrapAround =
      (newIndex === 0 && previousIndex === this.items.length - 1) ||
      (newIndex === this.items.length - 1 && previousIndex === 0)

    this.toggleItemVisibilityForWrapAround(isWrapAround)

    wrapper.scrollTo({
      left: wrapper.offsetWidth * newIndex,
      behavior: "smooth",
    })

    this.currentIndex = newIndex
  }

  toggleItemVisibilityForWrapAround(isWrapAround) {
    this.items.forEach((item, index) => {
      item.style.display =
        isWrapAround && index !== 0 && index !== this.items.length - 1
          ? "none"
          : ""
    })
  }

  handleIntersectionObserver(entries) {
    entries.forEach((entry) => {
      const elem = entry.target
      const intersectedItemIndex = this.items.findIndex(
        (item) => item === elem
      )

      if (entry.isIntersecting) {
        if (entry.intersectionRatio >= 0.75) {
          this.bulletTargets[intersectedItemIndex]?.classList?.add(
            this.activeClassValue
          )
        }
      } else {
        this.bulletTargets[intersectedItemIndex]?.classList?.remove(
          this.activeClassValue
        )
      }
    })
  }

  // Overlay methods
  initializeOverlay() {
    if (!this.hasInstructionsOverlayTarget) return

    // Set up auto-hide timeout
    if (this.hasInitialOverlayTimeoutValue) {
      this.overlayTimeout = setTimeout(() => {
        this.hideOverlay()
      }, this.initialOverlayTimeoutValue * 1000)
    }

    // Add touch/scroll listeners to hide overlay on swipe
    this.wrapperTarget.addEventListener("touchstart", this.hideOverlay.bind(this))
    this.wrapperTarget.addEventListener("scroll", this.hideOverlay.bind(this))
  }

  dismissOverlay(event) {
    event?.preventDefault()
    this.hideOverlay()
  }

  hideOverlay() {
    if (!this.hasInstructionsOverlayTarget) return

    clearTimeout(this.overlayTimeout)
    this.instructionsOverlayTarget.classList.add("carousel__instructions-overlay--hidden")

    // Remove the overlay from DOM after animation
    setTimeout(() => {
      if (this.hasInstructionsOverlayTarget) {
        this.instructionsOverlayTarget.style.display = "none"
      }
    }, 300)
  }
}
